import { decode } from 'html-entities';
import sanitizeHtml from 'sanitize-html';
import moment from 'moment/moment';
import { PROGRESS_COLORS, REDIRECT_FROM_SN } from '../storage';
import { currencyMap } from '../constants/currency-map';

const currentDate = new Date();

const urlRegex = /(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/[a-zA-Z0-9]{2,}|((https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?)|(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})?/i;
const exceptLettersRegex = new RegExp(/[^a-zA-Z]+/, 'gi');

const HOSTS_MAP = {
	'next.wonsulting.com': 'next',
	'app.wonsulting.com': 'production',
	'app2.wonsulting.com': 'production',
	'staging.wonsulting.com': 'staging'
};

const validateEmail = (email) => {
	return String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		);
};

const isValidHttpUrl = (value) => {
	return /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi.test(value);
};

const isValidUrl = (url) => {
	return urlRegex.test(url);
};

const copyToClipboard = (text) => {
	if (!navigator.clipboard) {
		return;
	}

	navigator.clipboard.writeText(text).then(() => {}, (err) => {
		console.error('Could not copy text: ', err);
	});
};

function componentToHex(c) {
	const hex = c.toString(16);
	return hex.length === 1 ? `0${  hex}` : hex;
}

function rgbToHex(r, g, b) {
	return `#${  componentToHex(r)  }${componentToHex(g)  }${componentToHex(b)}`;
}

function hexToRgb(string) {
	if (string.length !== 6) {
		throw 'Only six-digit hex colors are allowed.';
	}

	const aRgbHex = string.match(/.{1,2}/g);
	const aRgb = [
		parseInt(aRgbHex[0], 16),
		parseInt(aRgbHex[1], 16),
		parseInt(aRgbHex[2], 16)
	];
	return aRgb;
}

function makeid(length = 6) {
	let result = '';
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
	}
	return result;
}

const formatErrors = (errors) => {
	const formatted = {};

	Object.keys(errors).forEach((el) => {
		const value = errors[el];
		formatted[el] = !Array.isArray(value) ? [value] : value;
	});

	return formatted;
};

const popupwindow = (url, title, w, h) => {
	const left = (screen.width/2)-(w/2);
	const top = (screen.height/2)-(h/2);
	return window.open(url, title, `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${top}, left=${left}`);
}; 

const roundNearest = (num, tick = 15) => {
	return Math.round(num / tick) * tick;
};

const sort_by_key = (array, key) => {
	return array.sort((a, b) => {
		const x = a[key]; const y = b[key];
		return ((x < y) ? -1 : ((x > y) ? 1 : 0));
	});
};

const decodeHtml = (html) => {
	const txt = document.createElement('textarea');
	txt.innerHTML = html;
	return txt.value;
};

function extractContent(s) {
	const span = document.createElement('span');
	span.innerHTML = s;
	return span.textContent || span.innerText;
}

const decodeHtmlEntities = (text) => {
	return decode(text ??  '').replace(/<br\s*\/?>/gi, '\n');
};

const calcScreenDPI = () => {
	const el = document.createElement('div');
	el.style = 'width: 1in;';
	document.body.appendChild(el);
	const dpi = el.offsetWidth;
	document.body.removeChild(el);
  
	return dpi;
};
  
const getHeightForA4Page = (ppiHeight) => {
	const sizes = {
		72: 842,
		96: 1123,
		150: 1754,
		300: 3508,
		600: 7016,
	};
  
	return sizes?.[ppiHeight] ?? sizes[72];
};

const isInViewport = (element) => {
	const rect = element?.getBoundingClientRect();

	if (rect != null) {
		return (
			rect.top >= 0 &&
        rect.left >= 0 && 
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
		);
	}

	return false;
};

function debounce(func, timeout = 300) {
	let timer;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => { func.apply(this, args); }, timeout);
	};
}

//initialize throttlePause variable outside throttle function
let throttlePause;
 
const throttle = (callback, time) => {
	//don't run the function if throttlePause is true
	if (throttlePause) {return;}
 
	//set throttlePause to true after the if condition. This allows the function to be run once
	throttlePause = true;
   
	//setTimeout runs the callback within the specified time
	setTimeout(() => {
		callback();
     
		//throttlePause is set to false once the function has been called, allowing the throttle function to loop
		throttlePause = false;
	}, time);
};

const capitalizeFirstLetterEveryWord = (string) => {
	const words = string.split(' ');
    
	return words.map((word) => { 
		return word[0].toUpperCase() + word.substring(1); 
	}).join(' ');
};

const capitalizeFirstLetter = (string) => {
	return string.charAt(0).toUpperCase() + string.slice(1);
};

const arraytoList = (array) => {
	// Create the list element:
	const list = document.createDocumentFragment('ul');
 
	for (let i = 0; i < array.length; i++) {
		// Create the list item:
		const item = document.createElement('li');
 
		// Set its contents:
		item.appendChild(document.createTextNode(array[i]));
 
		// Add it to the list:
		list.appendChild(item);
	}
 
	// Finally, return the constructed list:
	return list;
};

const listToArray = (string, element = 'p') => {
	const div = document.createElement('div');
	div.innerHTML = string;
  
	const list = div.getElementsByTagName(element);
  
	const data = [];
  
	for (let i=0; i<list.length;i++) {
		data.push(list[i].innerHTML);
	}
  
	return data;
};

const createBpIdFrom = (str, id, index) => {
	return `highlight-${str.replace(exceptLettersRegex, '')}-${id}-${index}`;
};

const checkIfEmpty = (str) => {
	return str === '' || !str || str === null || str === undefined;
};

function bytesToMegaBytes(bytes) { 
	return bytes / (1024*1024); 
}

function truncateString(str, num) {
	if (str.length > num) {
		return `${str.slice(0, num)  }...`;
	} 
	return str;
	
}

function hashInput(input) {
	let hash = 0;
	const len = input.length;

	for (let i = 0; i < len; i++) {
		hash  = ((hash << 5) - hash) + input.charCodeAt(i);
		hash |= 0; // to 32bit integer
	}
	return hash;
}

const isDateInThisMonth = (date) => {
	if (isValidDate(date)) {
		const creationDate = new Date(date);

		if (
			currentDate.getMonth() === creationDate.getMonth()
			&& currentDate.getFullYear() === creationDate.getFullYear()
		) {
			return true;
		}
	}

	return false;
};

const isValidDate = (date) => {
	if (isNaN(Date.parse(date))) {
		return false;
	}
  
	return true;
};

function isOneWeekPassedFrom(givenDate) {
	if (!isValidDate(givenDate)) {
		return false;
	}

	const givenTime = new Date(givenDate).getTime();
	const currentTime = new Date().getTime();

	const oneWeekInMillis = parseInt(import.meta.env.VITE_SHOW_NUDGES_AFTER);

	const timeDifference = currentTime - givenTime;

	if (timeDifference >= oneWeekInMillis) {
		return true;
	} 
	return false;
	
}

const filterNumericInput = (evt) => {
	const event = evt ? evt : window.event;

	const charCode = (event.which) ? event.which : event.keyCode;

	if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
		event.preventDefault();
	} else {
		return true;
	}
};

const formatUrl = (rawUrl) => {
	let url = rawUrl;

	if (url == null || url?.toLowerCase()?.trim() === 'n/a') {
		return url;
	}

	if (url === '') {
		return null;
	}
    
	if (!url.startsWith('https://')) {
		url = `https://${  url}`;
	}

	if (!url.startsWith('https://www.') && !url.includes('www.')) {
		url = url.replace('https://', 'https://www.');
	}

	return url;
};

const appInDevMode = () => {
	return import.meta.env.DEV;
};

/**
 * True when the server rendered an authenticated session id into the SPA mount element
 * (welcome.blade.php: <div id="app" data-user-id="{{ Auth::id() }}">). Lets the SPA hydrate
 * the user from /api/user when it arrives from the Inertia onboarding handoff with a valid
 * session but no cached localStorage user.
 */
const hasServerSession = () => {
	return Boolean(document.getElementById('app')?.dataset.userId);
};

/**
 * The URL prefixes Inertia serves right now, one per switched-on section, as the server
 * rendered them on the SPA mount element (welcome.blade.php: <div id="app" data-inertia-sections>).
 */
const inertiaSectionPaths = () => {
	try {
		const paths = JSON.parse(document.getElementById('app')?.dataset.inertiaSections ?? '[]');

		return Array.isArray(paths) ? paths : [];
	} catch {
		return [];
	}
};

/**
 * True when the path belongs to a switched-on Inertia section, so the server, not the SPA,
 * renders it and the SPA must reach it with a full page load.
 */
const isInertiaPath = (path) => {
	return inertiaSectionPaths().some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
};

const secondsToTime = (timeInSeconds, withWords=false, appendZero=true) => {
	let minutes = Math.floor(timeInSeconds / 60);
	let seconds = Math.round(timeInSeconds - minutes * 60);

	if (minutes < 10 && appendZero) {
		minutes = `0${minutes}`;
	}

	if (seconds < 10 && appendZero) {
		seconds = `0${seconds}`;
	}

	if (withWords) {
		return `${minutes} minutes ${Math.round(seconds)} seconds`;
	}

	return `${minutes}:${Math.round(seconds)}`;
};

function formatSecondsToTime(seconds) {
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${mins}:${String(secs).padStart(2, '0')}`;
}

const getColorForProgress = (progress) => {
	let numericalValue;

	try {
		numericalValue = parseInt(progress);
	} catch {
		return PROGRESS_COLORS.gray;
	}

	if (isNaN(numericalValue)) {
		return PROGRESS_COLORS.gray;
	}

	if (numericalValue < 30) {
		return PROGRESS_COLORS.gray;
	} else if (numericalValue < 50) {
		return PROGRESS_COLORS.red;
	} else if (numericalValue < 75) {
		return PROGRESS_COLORS.yellow;
	}

	return PROGRESS_COLORS.green;
};

const openLink = (url, inNewPage = false) => {
	const link = document.createElement('a');
	link.href = url;

	if (inNewPage) {
		link.target = '_blank';
	}

	document.body.appendChild(link);

	link.click();
	link.remove();
};

const fixContentFormat = (content) => {
	if (content == null || content === '<ul></ul>' ) {return null;}

	let result;

	//decode html entities
	result = decode(content);

	// replace backslashes
	result = result.replace(/<\\/g, '<');

	//remove quote symbol
	if (result[0] === '"') {
		result = result.slice(1);
	}

	if (result[result.length - 1] === '"') {
		result = result.slice(0, result.length - 1);
	}

	return result;
};

const formatNumberWithCommas = (number) => {
	return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatNumberToK = (number) => {
	if (number % 1000 === 0 && number >= 1000) {
		return `${(number / 1000).toFixed(0)  }K`;
	}

	return number.toString();
};

const getAppEnvFromHost = () => {
	const host = location.host;

	return HOSTS_MAP?.[host] || 'unknown';
};

const isSocialMediaAppBrowser = () => {
	const ua = navigator.userAgent.toLowerCase().trim();
	return REDIRECT_FROM_SN.filter((sn) => ua.includes(sn)).length > 0;
};

const getDaysDiffAfter = (date) => {
	const startDate = moment(date);
	const now = moment();

	return now.diff(startDate, 'days');
};

const formatRelativeDate = (value, fallback = '—') => {
	if (!value) {
		return fallback;
	}
	const m = moment(value);
	return m.isValid() ? m.fromNow() : fallback;
};

const formatUrlParams = (params) => {
	if (params === '' || params == null) {
		return '';
	}

	const paramsPairs = Object.keys(params).map((key) => {
		return `${key}=${encodeURIComponent(params[key])}`;
	});

	return `?${paramsPairs.join('&')}`;
};

const getWorkExperienceWithPoints = (currentSelectedResume) => {
	if (!currentSelectedResume || !currentSelectedResume.sections) {return '';}

	const workExperienceSection = currentSelectedResume.sections.find(
		(section) => section.type === 'work_experience'
	);

	if (!workExperienceSection || !workExperienceSection.items) {return '';}

	const workExperienceText = workExperienceSection.items
		.map((item) => {
			// Extract heading and title, cleaning HTML tags
			const heading = item.heading
				? item.heading.replace(/<\/?[^>]+(>|$)/g, '').trim()
				: '';
			const title = item.title
				? item.title.replace(/<\/?[^>]+(>|$)/g, '').trim()
				: '';

			// Extract and clean bullet points
			const points = item.bullet_points
				? item.bullet_points
					.replace(/<\/?p>/g, '') // Remove <p> tags
					.replace(/<\/?ul>|<\/?li>/g, '') // Remove <ul> and <li> tags
					.trim()
				: '';

			// Combine heading, title, and points
			let entryText = heading ? `Company: ${heading}` : '';
			entryText += title ? `\nTitle: ${title}` : '';
			entryText += points ? `\nPoints:\n${points}` : '';

			return entryText.trim(); // Return the formatted entry
		})
		.filter(Boolean)
		.join('\n\n');

	return workExperienceText ?? '';
};

const getCommunityResumeLink = (resumeId) => {
	return `${location.origin}/community?resume=${resumeId}`;
};

const displayYear = (value) => {
	if (value === 1) {
		return 'year';
	} else if (value < 10) {
		return 'years';
	} 
	return ' +years';
};

const sanitizeHtmlText = (text) => {
	return sanitizeHtml(text, { allowedTags: [] });
};

const getFilterByValue = (filterOptions, item) => {
	const response = filterOptions.find((el) => el.value === item);
	return response;
};

const formatPrice = (amount, currency) => {
	if (!amount) {return '';}
	const formattedAmount = (amount / 100).toFixed(2);
	const currencyData = currencyMap[currency] || {};

	const response = currencyData.symbol 
		? `${currencyData.symbol}${formattedAmount} ${currencyData.abbreviation}`
		: formattedAmount;

	return response; 
};

const delay = (ms = 100) => {
	return new Promise((resolve) => setTimeout(resolve, ms));
};

const getPeriodTextLabel = (dateStr) => {
	if (!dateStr || dateStr === '') {return null;}

	const periodDate = moment(dateStr, 'YYYY-MM-DD', true).isValid()
		? moment(dateStr, 'YYYY-MM-DD')
		: moment(dateStr);

	if (!periodDate.isValid()) {return '';}

	const now = moment();

	if (periodDate.isSame(now, 'day')) {return 'Today';}
	if (periodDate.isSame(now, 'isoWeek')) {return 'This week';}
	if (periodDate.isSame(now, 'month')) {return 'This month';}
	if (periodDate.isSame(now.clone().subtract(1, 'month'), 'month')) {return 'Last month';}

	return periodDate.format('MMM YYYY');
};

const escapeRegExp = (str) => str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const getStorageJson = (key, fallback = []) => {
	try {
		const parsed = JSON.parse(localStorage.getItem(key) ?? JSON.stringify(fallback));
		return Array.isArray(fallback) ? (Array.isArray(parsed) ? parsed : fallback) : parsed;
	} catch {
		return fallback;
	}
};

export {
	escapeRegExp,
	hashInput,
	bytesToMegaBytes,
	truncateString,
	extractContent,
	checkIfEmpty,
	hexToRgb,
	rgbToHex,
	isValidHttpUrl,
	listToArray,
	arraytoList,
	capitalizeFirstLetterEveryWord,
	capitalizeFirstLetter,
	throttle,
	debounce,
	isInViewport,
	calcScreenDPI,
	getHeightForA4Page,
	decodeHtmlEntities,
	decodeHtml,
	sort_by_key,
	roundNearest,
	formatErrors,
	validateEmail,
	copyToClipboard,
	popupwindow,
	makeid,
	isDateInThisMonth,
	isValidUrl,
	filterNumericInput,
	formatUrl,
	createBpIdFrom,
	appInDevMode,
	hasServerSession,
	inertiaSectionPaths,
	isInertiaPath,
	secondsToTime,
	formatSecondsToTime,
	getColorForProgress,
	openLink,
	isOneWeekPassedFrom,
	fixContentFormat,
	formatNumberWithCommas,
	formatNumberToK,
	getAppEnvFromHost,
	getDaysDiffAfter,
	formatRelativeDate,
	isSocialMediaAppBrowser,
	formatUrlParams,
	getWorkExperienceWithPoints,
	getCommunityResumeLink,
	displayYear,
	sanitizeHtmlText,
	getFilterByValue,
	formatPrice,
	getPeriodTextLabel,
	delay,
	getStorageJson
};
